import theme from 'src/theme';

export const chartSx = {
    root: {
        mt: 1.5,
        borderRadius: '14px',
        border: '1px solid rgba(0, 0, 0, 0.06)',
        overflow: 'hidden',
        bgcolor: theme.palette.common.white,
        boxShadow: '0 1px 2px rgba(0,0,0,0.03), 0 4px 12px rgba(0,0,0,0.03)',
    },
    header: {
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        pt: 1.5,
        pb: 1,
        flexWrap: 'wrap',
    },
    titleWrap: {
        display: 'flex',
        flexDirection: 'column',
        gap: 0.25,
        minWidth: 0,
    },
    title: {
        fontSize: '0.82rem',
        fontWeight: 600,
        color: theme.palette.neutral[800],
        letterSpacing: '-0.01em',
    },
    subtitle: {
        fontSize: '0.68rem',
        fontWeight: 700,
        color: theme.palette.neutral[400],
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    },
    totals: {
        display: 'flex',
        gap: 2,
        flexWrap: 'wrap',
        alignItems: 'baseline',
    },
    totalItem: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'flex-end',
        gap: 0.25,
    },
    totalLabel: {
        fontSize: '0.62rem',
        fontWeight: 700,
        color: theme.palette.neutral[400],
        textTransform: 'uppercase',
        letterSpacing: '0.08em',
    },
    totalValue: {
        fontSize: '0.95rem',
        fontWeight: 700,
        color: theme.palette.neutral[800],
        letterSpacing: '-0.01em',
        fontVariantNumeric: 'tabular-nums',
    },
    metricTabs: {
        display: 'flex',
        gap: 0.5,
        px: 2,
        pb: 1,
        flexWrap: 'wrap',
    },
    metricTab: {
        px: 1.25,
        py: 0.5,
        borderRadius: '8px',
        fontSize: '0.72rem',
        fontWeight: 600,
        cursor: 'pointer',
        transition: 'all 0.15s ease',
        color: theme.palette.neutral[500],
        bgcolor: 'transparent',
        border: '1px solid transparent',
        '&:hover': {
            bgcolor: theme.palette.neutral[50],
        },
    },
    metricTabActive: {
        px: 1.25,
        py: 0.5,
        borderRadius: '8px',
        fontSize: '0.72rem',
        fontWeight: 600,
        cursor: 'pointer',
        color: theme.palette.common.white,
        border: '1px solid transparent',
        transition: 'all 0.15s ease',
    },
    // The chart lives in an absolutely-positioned box inside this relative,
    // fixed-size, clipped wrapper. Recharts' ResponsiveContainer measures its
    // parent and stamps an explicit pixel width on the SVG; on a bulk mount
    // (session restore) it can mis-measure and runaway-grow, which normally
    // propagates up and widens the whole page (defeating ancestor overflow).
    // Absolute positioning removes the chart from flow so it can NEVER
    // contribute to any ancestor's width — the feedback loop can't escape.
    chartWrapper: {
        position: 'relative',
        width: '100%',
        height: 220,
        boxSizing: 'border-box',
        overflow: 'hidden',
    },
    chartInner: {
        position: 'absolute',
        top: 0,
        left: 8,
        right: 8,
        bottom: 8,
    },
    tooltipCard: {
        bgcolor: theme.palette.common.white,
        border: '1px solid rgba(0,0,0,0.08)',
        borderRadius: '10px',
        boxShadow: '0 4px 12px rgba(0,0,0,0.1), 0 1px 3px rgba(0,0,0,0.06)',
        px: 1.5,
        py: 1,
        minWidth: 160,
    },
    tooltipDate: {
        fontSize: '0.78rem',
        fontWeight: 600,
        color: theme.palette.neutral[800],
        mb: 0.75,
        pb: 0.75,
        borderBottom: '1px solid rgba(0,0,0,0.06)',
    },
    tooltipRow: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        gap: 2,
        mb: 0.25,
        '&:last-of-type': {mb: 0},
    },
    tooltipLabel: {
        fontSize: '0.72rem',
        color: theme.palette.neutral[500],
    },
    tooltipValue: {
        fontSize: '0.78rem',
        fontWeight: 600,
        color: theme.palette.neutral[800],
        fontVariantNumeric: 'tabular-nums',
    },
    tooltipAnomaly: {
        fontSize: '0.7rem',
        fontWeight: 600,
        color: theme.palette.error.main,
        mt: 0.75,
        pt: 0.75,
        borderTop: '1px solid rgba(230, 41, 48, 0.15)',
        display: 'flex',
        alignItems: 'center',
        gap: 0.5,
    },
    footer: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 2,
        px: 2,
        py: 1,
        borderTop: '1px solid rgba(0, 0, 0, 0.04)',
        bgcolor: '#FAFBFD',
        flexWrap: 'wrap',
    },
    footerItem: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.75,
        lineHeight: 1,
    },
    footerLabel: {
        fontSize: '0.75rem',
        fontWeight: 500,
        color: theme.palette.neutral[500],
        lineHeight: 1,
    },
    footerValue: {
        fontSize: '0.75rem',
        fontWeight: 600,
        color: theme.palette.neutral[800],
        fontVariantNumeric: 'tabular-nums',
        lineHeight: 1,
    },
    legendDot: {
        display: 'inline-block',
        width: 8,
        height: 8,
        borderRadius: '50%',
        flexShrink: 0,
    },
    anomalyBadge: {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 0.75,
        py: 0.125,
        borderRadius: '6px',
        bgcolor: 'rgba(230, 41, 48, 0.08)',
        color: theme.palette.error.main,
        fontSize: '0.68rem',
        fontWeight: 600,
    },
};